import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import {
  Box,
  Button,
  InputAdornment,
  Link,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import {
  ActivityEvent,
  ActivityFilters,
  exportActivity,
  getActivity,
} from '../../api/activityApi/activityApi';
import Loader from '../../components/Loader';
import NoDataText from '../../components/NoDataText';
import TableHeader from '../../components/Table/TableHeader';
import TablePaginationContainer from '../../components/Table/TablePaginationContainer';
import { saveCsv } from '../../helpers/saveCsv';
import usePagination from '../../hooks/usePagination';
import useSnackBar from '../../hooks/useSnackBar';
import ActivityFiltersPanel, { ActivityPanelFilters } from './ActivityFiltersPanel';
import ActivityColumnsMenu, { ActivityColumn } from './ActivityColumnsMenu';
import ActivityEventDrawer from './ActivityEventDrawer';
import {
  ACTIVITY_TABS,
  ActivityTab,
  ENTITY_OPTIONS,
  activitySubtitle,
  activityTableColumns,
  activityTitle,
  buildActivityCsv,
  changedFieldNames,
  defaultDateRange,
  entityName,
  formatEventTime,
  isFailure,
} from './activityHelpers';
import { useActivityStyles } from './activityStyles';

const DEFAULT_COLUMNS: Record<ActivityTab, ActivityColumn[]> = {
  users: ['entity', 'record'],
  claimants: ['matter', 'entity', 'record'],
  system: ['entity', 'record'],
};

const columnsStorageKey = (tab: ActivityTab) => `activity.columns.${tab}`;

const readColumns = (tab: ActivityTab): ActivityColumn[] => {
  try {
    const stored = localStorage.getItem(columnsStorageKey(tab));
    return stored ? (JSON.parse(stored) as ActivityColumn[]) : DEFAULT_COLUMNS[tab];
  } catch {
    return DEFAULT_COLUMNS[tab];
  }
};

const writeColumns = (tab: ActivityTab, columns: ActivityColumn[] | null) => {
  try {
    if (columns) localStorage.setItem(columnsStorageKey(tab), JSON.stringify(columns));
    else localStorage.removeItem(columnsStorageKey(tab));
  } catch {
    // Column choice is a convenience; ignore storage failures.
  }
};

const initialPanelFilters = (): ActivityPanelFilters => ({
  ...defaultDateRange(),
  actorId: '',
  caseId: null,
  operation: '',
  category: '',
  failuresOnly: false,
  entities: [],
});

const ActivityView = () => {
  const { classes, cx } = useActivityStyles();
  const navigate = useNavigate();
  const [selectedEvent, setSelectedEvent] = useState<ActivityEvent | null>(null);
  const { setAlert } = useSnackBar();

  const [tab, setTab] = useState<ActivityTab>('users');
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [panelFilters, setPanelFilters] = useState<ActivityPanelFilters>(initialPanelFilters);
  const [columns, setColumns] = useState<ActivityColumn[]>(() => readColumns('users'));

  const { pageNumber, rowsPerPage, totalCount, setTotalCount, setPageNumber } = usePagination();
  const [events, setEvents] = useState<ActivityEvent[]>([]);
  const [failureCount, setFailureCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const requestId = useRef(0);

  const tabConfig = ACTIVITY_TABS.find((t) => t.value === tab) ?? ACTIVITY_TABS[0];

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(timer);
  }, [search]);

  const filters: ActivityFilters = useMemo(
    () => ({
      from: panelFilters.from,
      to: panelFilters.to,
      search: debouncedSearch,
      actorTypes: tabConfig.actorTypes,
      actorId: panelFilters.actorId || undefined,
      caseId: panelFilters.caseId ?? undefined,
      operation: panelFilters.operation || undefined,
      categories: panelFilters.category ? [panelFilters.category] : undefined,
      failuresOnly: panelFilters.failuresOnly,
      subjectTypes: ENTITY_OPTIONS.filter((o) => panelFilters.entities.includes(o.label)).flatMap(
        (o) => o.subjectTypes,
      ),
    }),
    [panelFilters, debouncedSearch, tabConfig],
  );
  const shownFilters = useRef(filters);

  // The page; back to the first one whenever the tab or any filter changes. The counts
  // come with the first page.
  useEffect(() => {
    if (shownFilters.current !== filters) {
      shownFilters.current = filters;
      if (pageNumber !== 1) {
        setPageNumber?.(1);
        return;
      }
    }
    const current = ++requestId.current;
    const withCount = pageNumber === 1;
    setIsLoading(true);
    getActivity(filters, { page: pageNumber, size: rowsPerPage, count: withCount })
      .then((res) => {
        if (current !== requestId.current) return;
        setEvents(res.data.resultSet);
        if (withCount) {
          setTotalCount?.(res.data.totalCount ?? 0);
          setFailureCount(res.data.failureCount ?? null);
        }
      })
      .catch(() => {
        if (current !== requestId.current) return;
        setEvents([]);
        setAlert?.({ message: 'Could not load activity. Please try again.', type: 'error' });
      })
      .finally(() => current === requestId.current && setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, pageNumber, rowsPerPage]);

  const changeTab = (value: ActivityTab) => {
    setTab(value);
    setColumns(readColumns(value));
    setPanelFilters((prev) => ({ ...prev, actorId: '' }));
  };

  const changeColumns = (value: ActivityColumn[]) => {
    setColumns(value);
    writeColumns(tab, value);
  };

  // Reset unchecks every optional column (Time, the person and Activity always show).
  const resetColumns = () => {
    setColumns([]);
    writeColumns(tab, []);
  };

  // The table's columns (time, who, the chosen ones, the activity); the export writes the same.
  const tableColumns = activityTableColumns(columns, tabConfig.actorLabel);

  const exportCsv = () => {
    setIsExporting(true);
    exportActivity(filters)
      .then((res) => {
        // Only what the table shows: its visible columns, with the same text.
        saveCsv(buildActivityCsv(res.data.resultSet, tableColumns), 'Activity_List');
        setAlert?.({ message: 'Activity exported successfully.', type: 'success' });
      })
      .catch(() => setAlert?.({ message: 'Could not export activity.', type: 'error' }))
      .finally(() => setIsExporting(false));
  };

  const openPerson = (e: React.MouseEvent, event: ActivityEvent) => {
    // The name opens the person's timeline; the rest of the row opens the event details.
    e.stopPropagation();
    navigate(`/activity/${event.actorType}/${encodeURIComponent(event.actorId)}`);
  };

  const show = (column: ActivityColumn) => columns.includes(column);

  return (
    <Box className={classes.page}>
      <Typography className={classes.subtitle}>
        What your firm&apos;s users and claimants have been doing.
      </Typography>

      <Box className={classes.tabs} role="tablist">
        {ACTIVITY_TABS.map((t) => (
          <Button
            key={t.value}
            role="tab"
            aria-selected={tab === t.value}
            className={cx(classes.tab, tab === t.value && classes.tabActive)}
            onClick={() => changeTab(t.value)}
          >
            {t.label}
          </Button>
        ))}
      </Box>

      <Box className={classes.toolbar}>
        <TextField
          className={classes.search}
          placeholder="Search activity"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <Button
          className={cx(classes.toolbarButton, showFilters && classes.toolbarButtonActive)}
          startIcon={<TuneIcon />}
          onClick={() => setShowFilters((prev) => !prev)}
        >
          Filters
        </Button>
        <ActivityColumnsMenu visible={columns} onChange={changeColumns} onReset={resetColumns} />
        <Box className={classes.counts}>
          {!isLoading && (
            <span>
              {totalCount} {totalCount === 1 ? 'event' : 'events'}
            </span>
          )}
          {!!failureCount && (
            <span className={classes.failurePill}>
              {failureCount} {failureCount === 1 ? 'failure' : 'failures'}
            </span>
          )}
        </Box>
        <Button
          className={classes.toolbarButton}
          startIcon={<FileDownloadOutlinedIcon />}
          onClick={exportCsv}
          disabled={isExporting || !events.length}
        >
          Export
        </Button>
      </Box>

      {showFilters && (
        <ActivityFiltersPanel
          filters={panelFilters}
          onChange={setPanelFilters}
          actorLabel={tabConfig.actorLabel}
          actorTypes={tabConfig.actorTypes}
        />
      )}

      {isLoading ? (
        <Box className={classes.empty}>
          <Loader colorType="warning" />
        </Box>
      ) : events.length === 0 ? (
        <NoDataText marginTop="40px" />
      ) : (
        <Box className={classes.tableContainer}>
          <Box sx={{ minWidth: 'max-content', width: '100%' }}>
            <Table sx={{ tableLayout: 'auto', width: '100%' }}>
              <TableHeader
                headerLabels={tableColumns.map((column) => ({
                  accessor: column.key,
                  isSortable: false,
                  label: column.label,
                }))}
                activeSortField={null}
                setActiveSortField={() => undefined}
              />
              <TableBody className={classes.tableBody}>
                {events.map((event) => (
                  <TableRow
                    key={event.id}
                    onClick={() => setSelectedEvent(event)}
                    className={cx(isFailure(event) && classes.failedRow)}
                  >
                    <TableCell className={classes.time}>
                      {formatEventTime(event.occurredAt)}
                    </TableCell>
                    <TableCell className={classes.strong}>
                      <Tooltip title="View all activity by this person">
                        <Link
                          component="button"
                          color="inherit"
                          underline="hover"
                          className={classes.personLink}
                          onClick={(e) => openPerson(e, event)}
                        >
                          {event.actorName}
                        </Link>
                      </Tooltip>
                    </TableCell>
                    {show('matter') && (
                      <TableCell className={classes.time}>
                        {event.matter ? `#${event.matter.referenceId}` : '—'}
                      </TableCell>
                    )}
                    <TableCell className={classes.activityCell}>
                      <Box className={classes.strong}>
                        {activityTitle(event)}
                        {isFailure(event) && <span className={classes.failedBadge}>FAILED</span>}
                        {/* <span className={classes.categoryTag}>
                          {CATEGORY_LABELS[event.category] ?? event.category}
                        </span> */}
                      </Box>
                      <Tooltip title={changedFieldNames(event)} placement="bottom-start">
                        <Box className={classes.muted}>
                          {activitySubtitle(event)}
                          {event.changedFieldCount > 0 && (
                            <span className={classes.detailsLink}>View changes</span>
                          )}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    {show('entity') && (
                      <TableCell>
                        <span className={classes.entityPill}>{entityName(event.subjectType)}</span>
                      </TableCell>
                    )}
                    {show('record') && <TableCell>{event.subjectLabel ?? '—'}</TableCell>}
                    {show('outcome') && (
                      <TableCell>{isFailure(event) ? event.outcome : 'Success'}</TableCell>
                    )}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePaginationContainer />
          </Box>
        </Box>
      )}

      <ActivityEventDrawer event={selectedEvent} onClose={() => setSelectedEvent(null)} />
    </Box>
  );
};

export default ActivityView;
